import { ACTIVITY_DAYS, activityCells, activityRows, activityValue, compareActivity, extraEnergyPerDay, isActivityDays, LOADOUT_SLOTS, type ActivityDays, type ActivityRow, type EstimateFindAnswer, type PlayerDbAnswer, type PlayerEstimateAnswer, type PlayerHistoryEntry, type PlayerResponse, type PlayerSearchResult, type SharedLoadout } from '@omni-torn/shared';
import { formatDate, formatNumber, h, relativeTime } from '@omni-torn/ui';
import { api, ApiError } from '../../api';
import { navigateTo } from '../nav';
import { activityFromServer, activityWithMyKey, type ActivityRead } from '../player-stats';
import { server, session } from '../session';
import { ago, box, kids, loading, problem, short, signed, table, tabs, withTableCsv } from '../ui';
import { ESTIMATE_NOTE, estimateHistoryBox, estimateTiles, methodWord } from './estimate-view';
import { playerActivityBox } from './activity-view';
import { playerFlightsBox } from './flights-view';

/**
 * The player page (website roadmap S06; it began as the site's first feature, Player lookup): any player by name or id —
 *  - their profile from the Omni-Torn server (lent keys or its own key, cached, with provenance and history);
 *  - Omni-Torn's estimate of them with its source, age and history (the player database's estimates only — the same
 *    view as Spies & estimates, pages/estimate-view.ts);
 *  - the last loadout an Omni-Torn user saw them wear in a fight (terms 3), and their own if they share it with your faction;
 *  - what they did over 7/30/90 days (the extension's Activity lookup: Xanax, refills, time played, attacks…) — read live
 *    with your key from this browser, or by the Omni-Torn server on demand (capped, kept an hour there; app/player-stats.ts);
 *  - Compare: them beside any second player or yourself — estimates, level and the same activity numbers.
 * Nothing read here is kept by the website or sent anywhere; the owner also sees the database's summary of them.
 */

let view: HTMLElement;

export async function renderLookup(v: HTMLElement, path: string): Promise<void> {
  view = v;
  const id = /^\/lookup\/(\d+)\/?$/.exec(path)?.[1];
  const holder = h('div', {});
  v.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Player lookup')), searchForm(), holder);
  view = holder;
  if (id) await renderPlayer(Number(id));
  else holder.replaceChildren(h('p', { class: 'muted' }, 'Type a name (Enter asks Torn) or an id, or paste Torn’s “name [id]”.'));
}

// ---------------------------------------------------------------- search
//
// Typing shows names this server already knows (instant, free). Pressing Enter on a
// name also asks Torn's own player search (costs one Torn request), and the names it
// returns are remembered for next time. A number is treated as a player ID.

function searchForm(): HTMLElement {
  const input = h('input', { id: 'search-input', name: 'q', type: 'search', placeholder: 'Player name or ID…', maxlength: '32', autocomplete: 'off' }) as HTMLInputElement;
  const suggestions = h('ul', { class: 'suggestions', id: 'suggestions', role: 'listbox', hidden: true }) as HTMLUListElement;
  const form = h('form', { class: 'search', id: 'search-form', role: 'search', autocomplete: 'off' }, h('label', { class: 'visually-hidden', for: 'search-input' }, 'Player ID or name'), input, h('button', { type: 'submit' }, 'Look up'), suggestions);
  const hideSuggestions = () => (suggestions.hidden = true);
  const showMessage = (text: string) => {
    suggestions.replaceChildren(h('li', { class: 'muted empty' }, text));
    suggestions.hidden = false;
  };
  const showSuggestions = (results: PlayerSearchResult[], opts: { empty?: string; hint?: string; note?: string | undefined } = {}) => {
    suggestions.replaceChildren(
      ...results.map((r) =>
        h('li', { role: 'option' }, h('a', { href: `/lookup/${r.id}`, 'data-link': true, onclick: () => hideSuggestions() }, r.name, h('span', { class: 'muted' }, ` [${r.id}]${r.level !== null ? ` · Lv ${r.level}` : ''}`))),
      ),
    );
    if (results.length === 0 && opts.empty) suggestions.append(h('li', { class: 'muted empty' }, opts.empty));
    if (opts.note) suggestions.append(h('li', { class: 'muted empty' }, opts.note));
    if (opts.hint) suggestions.append(h('li', { class: 'hint' }, opts.hint));
    suggestions.hidden = suggestions.childElementCount === 0;
  };
  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const q = input.value.trim();
    clearTimeout(searchTimer);
    // Accept "1234", "[1234]" or Torn's "name [1234]" format.
    const id = /^\[?(\d+)\]?$/.exec(q)?.[1] ?? /\[(\d+)\]\s*$/.exec(q)?.[1];
    if (id) {
      hideSuggestions();
      return navigateTo(`/lookup/${id}`);
    }
    if (q.length < 2) return;
    showMessage(`Searching Torn for “${q}”…`);
    try {
      const res = await api.search(q, true);
      const exact = res.results.filter((r) => r.name.toLowerCase() === q.toLowerCase());
      if (exact.length === 1) {
        hideSuggestions();
        return navigateTo(`/lookup/${exact[0]!.id}`);
      }
      showSuggestions(res.results, { empty: res.searchedTorn ? `No Torn player found matching “${q}”.` : `No known player matching “${q}”.`, note: res.warning });
    } catch (err) {
      showMessage(err instanceof ApiError ? err.message : 'Search failed.');
    }
  });
  input.addEventListener('input', () => {
    clearTimeout(searchTimer);
    const q = input.value.trim();
    if (q.length < 2 || /^\d+$/.test(q)) return hideSuggestions();
    searchTimer = setTimeout(async () => {
      try {
        const res = await api.search(q, false);
        showSuggestions(res.results, { hint: `Press Enter to search Torn for “${q}”` });
      } catch {
        hideSuggestions();
      }
    }, 200);
  });
  input.addEventListener('keydown', (e) => e.key === 'Escape' && hideSuggestions());
  document.addEventListener('click', (e) => {
    if (!form.contains(e.target as Node)) hideSuggestions();
  });
  queueMicrotask(() => input.focus());
  return h('div', { class: 'lookup-bar' }, form);
}

// ---------------------------------------------------------------- player

async function renderPlayer(id: number, refresh = false): Promise<void> {
  document.title = `Player ${id} · Omni-Torn`;
  if (!refresh) view.replaceChildren(h('div', { class: 'panel loading', 'aria-busy': 'true' }, 'Loading player…'));
  let data: PlayerResponse;
  try {
    data = await api.player(id, refresh);
  } catch (err) {
    view.replaceChildren(errorPanel(err));
    return;
  }
  const historyPanel = h('section', { class: 'panel' }, panelHeader('History'), h('div', { class: 'panel-body muted' }, 'Loading…'));
  const estimate = h('div', {}, box('Estimate', loading(), { id: 'player-estimate' }));
  const loadout = h('div', {});
  const owner = h('div', {});
  const page = playerPage(id, data.player.name, data.player.level);
  // When they play (website roadmap S22): what Omni-Torn kept of their activity — no Torn call.
  const heat = h('div', {});
  // Flights (website roadmap S23): the flight tracker's current flight, landing windows and last flights — no Torn call.
  const flights = h('div', {});
  view.replaceChildren(playerPanel(data, () => void renderPlayer(id, true)), estimate, page.activity, heat, flights, page.compare, loadout, historyPanel, owner);
  view.focus({ preventScroll: true });
  document.title = `${data.player.name} [${id}] · Omni-Torn`;
  void estimateCard(id).then((c) => estimate.replaceChildren(...c));
  void loadoutCard(id).then((c) => loadout.replaceChildren(...kids(c)));
  void playerActivityBox(id).then((c) => heat.replaceChildren(c));
  void playerFlightsBox(id).then((c) => flights.replaceChildren(c));
  if (session.me()?.owner) void ownerCard(id).then((c) => owner.replaceChildren(...kids(c)));

  try {
    const { entries } = await api.history(id);
    historyPanel.replaceChildren(panelHeader('History'), historyTable(entries));
  } catch {
    historyPanel.replaceChildren(panelHeader('History'), h('div', { class: 'panel-body muted' }, 'History unavailable.'));
  }
}

// ---------------------------------------------------------------- estimate, loadout, the owner's view

/** Omni-Torn's estimate of them (the player database's estimates only) with its history — as Spies & estimates shows it. */
async function estimateCard(id: number): Promise<HTMLElement[]> {
  let a: PlayerEstimateAnswer;
  try {
    a = await estimateOf(id);
  } catch (err) {
    return [box('Estimate', problem(err), { id: 'player-estimate' })];
  }
  const more = h('a', { href: `/spies/player/${id}`, 'data-link': true }, 'Spies & estimates →');
  if (!a.player?.total && !a.spies.length) return [box('Estimate', [h('p', { class: 'muted' }, 'Omni-Torn has no estimate of them yet — their profile, a fight or a spy puts one in.'), h('p', {}, more)], { id: 'player-estimate' })];
  return [box('Estimate', [estimateTiles(a), h('p', { class: 'muted small' }, ESTIMATE_NOTE, ' ', more)], { id: 'player-estimate' }), ...(a.history.length > 1 ? [estimateHistoryBox(a, { id: 'player-estimate-history' })] : [])];
}

const estimates = new Map<number, Promise<PlayerEstimateAnswer>>();
/** One ask per player while the page is open (the compare box reuses it). */
function estimateOf(id: number): Promise<PlayerEstimateAnswer> {
  let p = estimates.get(id);
  if (!p) {
    p = server<PlayerEstimateAnswer>('GET', `/api/estimates/player/${id}`);
    estimates.set(id, p);
    p.catch(() => estimates.delete(id));
    setTimeout(() => estimates.delete(id), 5 * 60_000);
  }
  return p;
}

/** The newest loadout an Omni-Torn user saw them wear in a fight (a month; terms 3), and theirs if shared with your faction. */
async function loadoutCard(id: number): Promise<HTMLElement | null> {
  let r: { at: number | null; loadout: SharedLoadout | null; seen: { at: number; loadout: SharedLoadout } | null };
  try {
    r = await server('GET', `/api/faction/loadout?player=${id}`);
  } catch {
    return null;
  }
  const list = (l: SharedLoadout) =>
    table(
      [...l.items].sort((a, b) => LOADOUT_SLOTS.indexOf(a.slot) - LOADOUT_SLOTS.indexOf(b.slot)),
      [
        { label: 'Slot', get: (i) => i.slot },
        { label: 'Item', get: (i) => h('span', { class: i.rarity ? `rarity-${i.rarity}` : '' }, i.name) },
        { label: 'Dmg / acc / armor', get: (i) => [i.damage, i.accuracy, i.armor].filter((x) => x !== null && x !== undefined).join(' / ') || '—' },
        { label: 'Bonuses', get: (i) => (i.bonuses?.length ? i.bonuses.join(', ') : '—') },
      ],
    );
  const parts: Array<Node | string> = [];
  if (r.loadout && r.at) parts.push(h('h3', { class: 'sub' }, `Their own loadout, shared with your faction (${ago(r.at)})`), list(r.loadout));
  if (r.seen) parts.push(h('h3', { class: 'sub' }, `Seen in a fight by an Omni-Torn user ${ago(r.seen.at)}`), list(r.seen.loadout));
  if (!parts.length) parts.push(h('p', { class: 'muted' }, 'No Omni-Torn user has seen what they wear in the last month (it shows once someone fights them).'));
  else if (r.seen) parts.push(h('p', { class: 'small' }, h('a', { href: `/loadouts/${id}`, 'data-link': true, 'data-loadouts-link': '' }, 'Every change this month, item by item (Loadouts)')));
  return box('Loadout', parts, { id: 'player-loadout' });
}

/** The owner only: the database's summary of them (every kind it holds, how old). Nobody else sees this. */
async function ownerCard(id: number): Promise<HTMLElement | null> {
  try {
    const r = await server<PlayerDbAnswer>('GET', `/api/players/db?ids=${id}`);
    const p = r.players[0];
    if (!p) return box('Player database (only you see this)', h('p', { class: 'muted' }, 'Not in the database.'), { id: 'player-db' });
    const kinds = Object.entries(p.latest).sort((a, b) => b[1]!.at - a[1]!.at);
    return box(
      'Player database (only you see this)',
      [
        h('p', { class: 'muted small' }, `${formatNumber(p.count)} observations since ${formatDate(Math.floor(p.firstSeen / 1000))}. The newest of each kind:`),
        table(kinds, [
          { label: 'Kind', get: ([k]) => k },
          { label: 'When', get: ([, v]) => ago(v!.at), sort: ([, v]) => v!.at },
          { label: 'From', get: ([, v]) => v!.source },
          { label: 'Trust', get: ([, v]) => `${Math.round(v!.trust * 100)}%`, cls: 'r' },
          { label: 'What', get: ([, v]) => h('code', { class: 'small' }, JSON.stringify(v!.data).slice(0, 160)) },
        ]),
      ],
      { id: 'player-db' },
    );
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------- activity and compare

const SOURCE_KEY = 'omni-torn.playerStatsSource';
const DAYS_KEY = 'omni-torn.playerStatsDays';
const remembered = (k: string): string | null => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const remember = (k: string, v: string): void => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* this browser keeps nothing: fine */
  }
};

type Source = 'key' | 'server';
const sourceWords = (r: ActivityRead): string => (r.source === 'key' ? `Read with your key ${r.at > Date.now() - 60_000 ? 'just now' : ago(r.at)}` : `From the Omni-Torn server, read ${ago(r.at)} (kept there an hour)`);

/** The Activity box and the Compare box of one player page (they share the period and the way stats are read). */
function playerPage(id: number, name: string, level: number): { activity: HTMLElement; compare: HTMLElement } {
  const days0 = Number(remembered(DAYS_KEY));
  let days: ActivityDays = isActivityDays(days0) ? days0 : 30;
  let source: Source | null = null;
  let mine: ActivityRead | null = null;

  const read = async (who: number, how: Source): Promise<ActivityRead> => (how === 'key' ? activityWithMyKey(who, days) : (await activityFromServer(who, days))!);

  // ---- Activity
  const body = h('div', {});
  const status = h('p', { class: 'form-status', 'aria-live': 'polite' });
  const withKey = h('button', { type: 'button', class: 'btn-small', 'data-read': 'key', title: 'Two Torn calls with your key, from this browser' }, 'Read with my key') as HTMLButtonElement;
  const withServer = h('button', { type: 'button', class: 'btn-small', 'data-read': 'server', title: 'No calls of yours: Omni-Torn’s server asks Torn (kept there an hour)' }, 'Ask Omni-Torn') as HTMLButtonElement;
  const draw = (r: ActivityRead) => {
    const rows = activityRows(r.now, r.then, r.days);
    body.replaceChildren(
      ...kids(
        h('p', { class: 'muted small', 'data-activity': 'source' }, sourceWords(r), ` · ≈ +${formatNumber(extraEnergyPerDay(rows))} energy a day from Xanax & refills`),
        table(rows, [
          { label: 'Stat', get: (x) => x.label },
          { label: `Last ${r.days} days`, get: (x) => activityCells(x)[0], sort: (x) => x.period, cls: 'r' },
          { label: 'Per day', get: (x) => activityCells(x)[1], sort: (x) => x.perDay, cls: 'r' },
          { label: 'All time', get: (x) => activityCells(x)[2], sort: (x) => x.total, cls: 'r' },
        ]),
        r.then ? null : h('p', { class: 'muted small' }, `Torn had nothing for ${r.days} days ago (a newer account?), so the period shows all-time numbers.`),
        r.problem ? h('p', { class: 'notice' }, r.problem) : null,
      ),
    );
  };
  const load = async (how: Source, button?: HTMLButtonElement) => {
    source = how;
    remember(SOURCE_KEY, how);
    if (button) button.disabled = true;
    status.textContent = how === 'key' ? 'Asking Torn with your key…' : 'Asking the Omni-Torn server…';
    try {
      mine = await read(id, how);
      status.textContent = '';
      draw(mine);
    } catch (err) {
      status.textContent = (err as Error).message;
    } finally {
      if (button) button.disabled = false;
    }
  };
  withKey.addEventListener('click', () => void load('key', withKey));
  withServer.addEventListener('click', () => void load('server', withServer));
  const period = tabs(
    ACTIVITY_DAYS.map((d) => ({ id: String(d), label: `${d} days` })),
    String(days),
    (d) => {
      days = Number(d) as ActivityDays;
      remember(DAYS_KEY, d);
      if (source) void load(source);
      else void fromKept();
      if (other) void compareWith(other.id);
    },
  );
  /** Free: what the server already keeps (no Torn call anywhere). */
  const fromKept = async () => {
    const r = await activityFromServer(id, days, true).catch(() => null);
    if (r && !source) {
      mine = r;
      draw(r);
    } else if (!source) body.replaceChildren(h('p', { class: 'muted' }, 'Xanax, refills, cans, boosters, time played, attacks, ranked-war hits… over the period, per day and all time. Read them with your key (2 calls) or ask Omni-Torn (none of yours).'));
  };
  void fromKept();
  const activity = box('Activity', [h('div', { class: 'toolbar' }, period, withKey, withServer), status, body], { id: 'player-activity' });

  // ---- Compare
  let other: { id: number } | null = null;
  const cmpInput = h('input', { type: 'text', placeholder: 'Player id, name or “name [id]”', 'aria-label': 'Player to compare with', maxlength: '60' }) as HTMLInputElement;
  const cmpBtn = h('button', { type: 'submit', class: 'btn-small' }, 'Compare') as HTMLButtonElement;
  const meBtn = h('button', { type: 'button', class: 'btn-small' }, 'Compare with me') as HTMLButtonElement;
  const cmpStatus = h('span', { class: 'muted small', 'data-compare': 'status' });
  const cmpBody = h('div', {});
  const form = h('form', { class: 'compare-form', 'data-form': 'player-compare' }, cmpInput, cmpBtn, meBtn, cmpStatus);
  const idOf = async (raw: string): Promise<number | null> => {
    const q = raw.trim();
    const n = /^\[?(\d{1,9})\]?$/.exec(q)?.[1] ?? /\[(\d{1,9})\]\s*$/.exec(q)?.[1] ?? /(?:XID|ID)=(\d{1,9})/i.exec(q)?.[1];
    if (n) return Number(n);
    if (!/^[\w-]{1,30}$/.test(q)) return null;
    const r = await server<EstimateFindAnswer>('GET', `/api/estimates/find?name=${encodeURIComponent(q)}`).catch(() => null);
    const exact = r?.players.filter((p) => p.name.toLowerCase() === q.toLowerCase()) ?? [];
    return exact.length === 1 ? exact[0]!.id : null;
  };
  const compareWith = async (otherId: number) => {
    other = { id: otherId };
    const how: Source = source ?? (remembered(SOURCE_KEY) === 'server' ? 'server' : 'key');
    cmpBtn.disabled = meBtn.disabled = true;
    cmpStatus.textContent = how === 'key' ? 'Asking Torn with your key…' : 'Asking the Omni-Torn server…';
    try {
      const [a, b, ea, eb] = await Promise.all([mine && mine.days === days && mine.source === how ? mine : read(id, how), read(otherId, how), estimateOf(id).catch(() => null), estimateOf(otherId).catch(() => null)]);
      if (!source) {
        source = how;
        mine = a;
        draw(a);
      }
      const bName = otherId === session.get()?.playerId ? 'You' : (b.name ?? eb?.player?.name ?? `[${otherId}]`);
      cmpStatus.replaceChildren(`${name} beside `, otherId === session.get()?.playerId ? 'you' : h('a', { href: `/lookup/${otherId}`, 'data-link': true }, bName), ` · ${sourceWords(b)}.`);
      cmpBody.replaceChildren(compareTable({ name, level, act: a, est: ea }, { name: bName, level: b.level ?? eb?.player?.level ?? null, act: b, est: eb }, days));
    } catch (err) {
      cmpStatus.textContent = (err as Error).message;
    } finally {
      cmpBtn.disabled = meBtn.disabled = false;
    }
  };
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const otherId = await idOf(cmpInput.value);
    if (!otherId) {
      cmpStatus.textContent = 'Type their id (the number in their profile link), or a name Omni-Torn knows.';
      return;
    }
    void compareWith(otherId);
  });
  meBtn.addEventListener('click', () => {
    const me = session.get()?.playerId;
    if (me) void compareWith(me);
  });
  const compare = box('Compare', [form, h('p', { class: 'muted small' }, 'Beside any other player or yourself: level, Omni-Torn’s estimates and what each did over the period — read the way you picked above (your key, or the Omni-Torn server). Nothing is kept.'), cmpBody], { id: 'player-compare' });
  return { activity, compare };
}

interface Side {
  name: string;
  level: number | null;
  act: ActivityRead;
  est: PlayerEstimateAnswer | null;
}

function compareTable(a: Side, b: Side, days: ActivityDays): HTMLElement {
  const ra = activityRows(a.act.now, a.act.then, days);
  const rb = activityRows(b.act.now, b.act.then, days);
  const diff = (n: number | null, f: (x: number) => string) => h('span', { class: n && n > 0 ? 't-green' : n && n < 0 ? 't-red' : 'muted' }, n === null ? '—' : signed(n, f));
  const estA = a.est?.player?.total ?? null;
  const estB = b.est?.player?.total ?? null;
  const head: Array<{ label: string; a: string; b: string; d: HTMLElement }> = [
    { label: 'Level', a: a.level !== null ? String(a.level) : '—', b: b.level !== null ? String(b.level) : '—', d: diff(a.level !== null && b.level !== null ? a.level - b.level : null, (x) => String(x)) },
    {
      label: 'Estimate',
      a: estA ? `${short(estA)} (${methodWord(a.est!.player!.totalSource)})` : '—',
      b: estB ? `${short(estB)} (${methodWord(b.est!.player!.totalSource)})` : '—',
      d: diff(estA && estB ? estA - estB : null, (x) => short(x)),
    },
  ];
  const rows = compareActivity(ra, rb);
  const one = (r: ActivityRow) => activityValue(r.stat, r.period);
  return withTableCsv(h(
    'div',
    { class: 'table-wrap', 'data-testid': 'player-compare-table' },
    h(
      'table',
      { class: 'stat-table' },
      h('thead', {}, h('tr', {}, h('th', {}, ''), h('th', { class: 'r' }, `${a.name}, ${days} days`), h('th', { class: 'r' }, `${b.name}, ${days} days`), h('th', { class: 'r', title: `${a.name} minus ${b.name}` }, 'Difference'), h('th', { class: 'r' }, `${a.name}, all time`), h('th', { class: 'r' }, `${b.name}, all time`))),
      h(
        'tbody',
        {},
        ...head.map((x) => h('tr', {}, h('td', {}, x.label), h('td', { class: 'r' }, x.a), h('td', { class: 'r' }, x.b), h('td', { class: 'r' }, x.d), h('td', {}, ''), h('td', {}, ''))),
        ...rows.map((r) => h('tr', { 'data-stat': r.stat }, h('td', {}, r.label), h('td', { class: 'r' }, one(r.a)), h('td', { class: 'r' }, one(r.b)), h('td', { class: 'r' }, diff(r.period, (x) => activityValue(r.stat, x))), h('td', { class: 'r' }, activityValue(r.stat, r.a.total)), h('td', { class: 'r' }, activityValue(r.stat, r.b.total)))),
      ),
    ),
  ));
}

function panelHeader(title: string, ...extra: Array<Node | string>): HTMLElement {
  return h('header', { class: 'panel-head' }, h('h2', {}, title), ...extra);
}

function playerPanel({ player: p, provenance, warning }: PlayerResponse, onRefresh: () => void): HTMLElement {
  const statusClass = `status status-${p.status.color.replace(/[^a-z]/gi, '')}`;
  const life = p.life;
  const lifePct = life && life.maximum > 0 ? Math.round((life.current / life.maximum) * 100) : null;
  const refresh = h('button', { class: 'btn-small', type: 'button', title: 'Fetch the latest data from Torn' }, 'Refresh');
  refresh.addEventListener('click', () => {
    refresh.setAttribute('disabled', '');
    refresh.textContent = 'Refreshing…';
    onRefresh();
  });

  return h(
    'section',
    { class: 'panel player', 'data-testid': 'player-panel' },
    panelHeader(
      `${p.name} [${p.id}]`,
      h('a', { class: 'ext', href: `https://www.torn.com/profiles.php?XID=${p.id}`, target: '_blank', rel: 'noopener noreferrer' }, 'Open in Torn ↗'),
    ),
    warning ? h('div', { class: 'notice', role: 'status' }, warning) : null,
    h(
      'div',
      { class: 'panel-body' },
      h(
        'div',
        { class: 'player-top' },
        h(
          'div',
          {},
          h('div', { class: 'big-level' }, h('span', { class: 'label' }, 'Level'), String(p.level)),
        ),
        h(
          'div',
          { class: 'player-status' },
          h('span', { class: statusClass }, p.status.state),
          h('div', {}, p.status.description),
          p.status.details ? h('div', { class: 'muted' }, p.status.details) : null,
          p.lastAction
            ? h('div', { class: 'muted' }, `Last action: ${p.lastAction.status} · ${p.lastAction.relative}`)
            : null,
        ),
      ),
      lifePct !== null && life
        ? h(
            'div',
            { class: 'life', title: `Life ${formatNumber(life.current)} / ${formatNumber(life.maximum)}` },
            lifeBar(lifePct),
            h('span', {}, `Life ${formatNumber(life.current)} / ${formatNumber(life.maximum)}`),
          )
        : null,
      h(
        'dl',
        { class: 'facts' },
        ...fact('Rank', p.rank),
        ...fact('Title', p.title),
        ...fact('Faction', p.factionId ? `#${p.factionId}` : 'None'),
        ...fact('Age', p.ageDays !== null ? `${formatNumber(p.ageDays)} days` : null),
        ...fact('Signed up', p.signedUp ? formatDate(p.signedUp) : null),
        ...fact('Awards', formatNumber(p.awards)),
        ...fact('Friends / enemies', `${formatNumber(p.friends)} / ${formatNumber(p.enemies)}`),
        ...fact('Karma', formatNumber(p.karma)),
        ...fact('Forum posts', formatNumber(p.forumPosts)),
        ...fact('Spouse', p.spouse ? `${p.spouse.name} [${p.spouse.id}] · ${p.spouse.status}` : 'None'),
        ...fact('Property', p.property?.name ?? null),
        ...fact('Revivable', p.revivable === null ? null : p.revivable ? 'Yes' : 'No'),
        ...fact('Donator', p.donator ?? 'No'),
      ),
    ),
    h(
      'footer',
      { class: 'provenance', 'data-testid': 'provenance' },
      h('span', { class: `cert cert-${provenance.certainty}` }, capitalize(provenance.certainty)),
      h('span', {}, sourceLabel(provenance.source)),
      h(
        'span',
        { class: provenance.stale ? 'stale' : 'muted', title: formatDate(provenance.observedAt) },
        `updated ${relativeTime(provenance.observedAt)}${provenance.stale ? ' (stale)' : ''}`,
      ),
      refresh,
    ),
  );
}

/** Width set via CSSOM (allowed by our CSP), not an inline style attribute (blocked). */
function lifeBar(pct: number): HTMLElement {
  const bar = h('div', { class: 'life-bar' });
  bar.style.width = `${Math.min(100, Math.max(0, pct))}%`;
  return bar;
}

function fact(label: string, value: string | null | undefined): HTMLElement[] {
  return [h('dt', {}, label), h('dd', {}, value ?? '—')];
}

function historyTable(entries: PlayerHistoryEntry[]): HTMLElement {
  if (entries.length === 0) return h('div', { class: 'panel-body muted' }, 'No history yet.');
  if (entries.length === 1) {
    return h(
      'div',
      { class: 'panel-body muted' },
      `First recorded ${formatDate(entries[0]!.observedAt)}. Changes to level, rank, faction or awards will appear here.`,
    );
  }
  return withTableCsv(h(
    'div',
    { class: 'table-wrap' },
    h(
      'table',
      {},
      h('thead', {}, h('tr', {}, h('th', {}, 'When'), h('th', {}, 'Level'), h('th', {}, 'Rank'), h('th', {}, 'Faction'), h('th', {}, 'Awards'), h('th', {}, 'Name'))),
      h(
        'tbody',
        {},
        ...entries.map((e) =>
          h(
            'tr',
            {},
            h('td', { title: formatDate(e.observedAt) }, relativeTime(e.observedAt)),
            h('td', {}, String(e.level)),
            h('td', {}, e.rank ?? '—'),
            h('td', {}, e.factionId ? `#${e.factionId}` : '—'),
            h('td', {}, formatNumber(e.awards)),
            h('td', {}, e.name),
          ),
        ),
      ),
    ),
  ));
}

function errorPanel(err: unknown): HTMLElement {
  let title = 'Something went wrong';
  let detail = 'Please try again.';
  if (err instanceof ApiError) {
    detail = err.message;
    if (err.code === 'player_not_found') title = 'Player not found';
    else if (err.code === 'rate_limited') title = 'Slow down a little';
    else if (err.code === 'upstream_unavailable') title = 'Torn is not responding';
    else if (err.code === 'not_configured') title = 'Live lookups are off';
    else if (err.code === 'bad_request') title = 'That doesn’t look right';
  }
  return h('section', { class: 'panel error', role: 'alert' }, panelHeader(title), h('div', { class: 'panel-body' }, detail));
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ---------------------------------------------------------------- data mode

/** Set from /api/health. In mock mode every player is made up, and we say so everywhere. */
let dataMode: 'live' | 'mock' | 'unknown' = 'unknown';

function sourceLabel(source: string): string {
  if (source === 'torn_api') return dataMode === 'mock' ? 'Mock Torn API — made-up data' : 'Torn API';
  return source;
}

/** main.ts sets it from /api/health. */
export function setDataMode(m: 'live' | 'mock' | 'unknown'): void {
  dataMode = m;
}
